import { Fragment, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'

import { getWalletInfoQueryKey, type GetWalletInfoResponse } from '@/shared/api'
import { getIconName } from '@/shared/lib/get-icon-name'
import { AppsflyerEvent, sendAppsflyer } from '@/shared/metrics'
import { Icon } from '@/shared/ui'

import { type TFormValues, useMetrics } from '../../model'

import styles from './bottom-sheet-radio.module.scss'

type TBottomSheetRadioProps = {
    triggerClose: () => void
}

export const BottomSheetRadio = ({ triggerClose }: TBottomSheetRadioProps): React.ReactNode => {
    const queryClient = useQueryClient()
    const { financeTopupMethodChangedClickEvent } = useMetrics({
        formMethods: useFormContext<TFormValues>(),
    })
    const data = queryClient.getQueryData<GetWalletInfoResponse>(getWalletInfoQueryKey())
    const { watch, setValue } = useFormContext<TFormValues>()

    // Мемоизация преобразованных методов оплаты
    const paymentMethods = useMemo(() => {
        if (!data?.payment_methods) {
            return []
        }

        return data.payment_methods
    }, [data])

    return (
        <div className={styles['bottom-sheet-radio']}>
            {paymentMethods.map((method) => (
                <Fragment key={method.id}>
                    {method.is_available && (
                        <button
                            key={method.id}
                            data-test-id={`driver-recharge-method-option-${method.id}`}
                            type='button'
                            className={clsx(
                                styles['bottom-sheet-radio__item'],
                                watch('paymentMethod')?.id === method.id &&
                                    styles['bottom-sheet-radio__item_selected'],
                            )}
                            onClick={() => {
                                if (watch('paymentMethod')?.id !== method.id) {
                                    sendAppsflyer(
                                        AppsflyerEvent.finance_topup_method_changed_click,
                                        financeTopupMethodChangedClickEvent(method.id),
                                    )
                                    setValue('paymentMethod', method)
                                }
                                triggerClose()
                            }}
                        >
                            <div className={styles['bottom-sheet-radio__item-icon']}>
                                <Icon icon={getIconName(method.id)} size={24} />
                            </div>
                            <span className={styles['bottom-sheet-radio__item-text']}>
                                {method.title}
                            </span>
                            {watch('paymentMethod')?.id === method.id && (
                                <div className={styles['bottom-sheet-radio__item-icon']}>
                                    <Icon icon='check' size={24} />
                                </div>
                            )}
                        </button>
                    )}
                </Fragment>
            ))}
        </div>
    )
}
